import * as React from 'react';
import { DivergingBars, gradeName, MoveGradeLegend, signed, SkeletonPlot, SkeletonRows, TextSkeleton } from '@chess/api-insights/charts';
import { ENDING_RESULT_VIEWS, endingCaseView, endingResultView } from '@chess/api-ending-case';
import { GROUND_CAUSE_VIEWS, groundCauseView } from '@chess/api-mistakes';
import { MOVE_GRADES, TIME_SLIP_SECONDS, type Color, type Fen, type GradedMove, type GroundCause, type Phase, type San, type Uci } from '@chess/api-model';
import { lineTail, numberedMove } from '@chess/api-insights';
import { stagePly, type GameHabit, type HistoryPoint, type LastGame, type LastGameResult, type StageStatus } from './LastGameAggregator.ts';
import type { TypeRecord } from './stage-detail.ts';
import type { StageResult } from './stage-result.ts';
import { stageLabel } from './stage-label.ts';
import { shownRows } from './history-rows.ts';
import { CardEmpty, useDashboardParts, type DashboardCardProps } from '@chess/api-dashboard/react';
import './last-game.css';

// The per-game section of a stage, drawn from props only: the selected game
// (the game bar's, the latest without one) in the stage as three chart
// cards, the whole section a way into that game's review at the stage. A
// game that ended before the stage, or is not scanned yet, says so rather
// than showing another game. Three cards per stage (2026-10-08): the
// stage's result (its classification over a track from lost to winning, the
// start and the end of the stage marked), the score through the stage with
// the mistakes marked, and one card the stage's own: the game against the
// same family's average ("Your opening"), the middlegame's win chance lost
// to each cause with the usual cause under it ("Where it went wrong"), the
// ending's case with the period's record in that type ("The ending"). The
// opening's variation and the ending's type are the heading's (2026-10-10).
// The earlier Errors, "This game vs your average" and "Your openings" cards
// went on 2026-10-08.

// The section's heading is the stage alone (2026-10-10; "This game's opening ·
// vs rival · Oct 2 · Won" before): Home's title above already names the game.
// `latest` stays the section's accessible name. After it, muted in the small
// role, what the stage was in this game and its moves (`stageLabel`, also
// 2026-10-10): "Opening · Sicilian Defense · Alapin · moves 1–4", the
// opening's family in the ink colour (2026-10-10; "Sicilian Defense: Alapin"
// as one muted part before); the cards' headlines no longer repeat the name.
const STAGE: Record<Phase, { name: string; heading: string; latest: string }> = {
  opening: { name: 'opening', heading: 'Opening', latest: "This game's opening" },
  middlegame: { name: 'middlegame', heading: 'Middlegame', latest: "This game's middlegame" },
  ending: { name: 'ending', heading: 'Ending', latest: "This game's ending" },
};

/** What the section says without a game to draw: why the selected game is not shown. */
function missingText(stage: Phase, status: StageStatus | undefined, hasGames: boolean): string {
  if (!hasGames) return 'No games in the last 30 days.';
  if (status === 'unscanned') return "This game isn't scanned yet; it appears once it is.";
  return `This game ended before the ${STAGE[stage].name}.`;
}

const OUTCOME = { win: 'Won', draw: 'Drawn', loss: 'Lost' } as const;
const date = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const moveNo = (ply: number) => `${Math.ceil(ply / 2)}${ply % 2 ? '.' : '…'}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** A whole number with its sign: "+12", "−3", "0". */
const signedInt = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n)}`;
// A score change in the app's usual colours: a gain of 0.05 pawns or more good,
// a loss as large bad, anything smaller level (the ink colour).
const scoreTone = (pawns: number) => (pawns >= 0.05 ? ' last-game-good' : pawns <= -0.05 ? ' last-game-bad' : '');

// Phosphor CaretRight, regular weight.
const CARET_RIGHT = 'M181.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z';

export type LastGameSectionProps = {
  stage: Phase;
  /** Also the second row, this game against the reader's history (the stage screens; Home shows only the game). */
  history?: boolean;
} & (
  | { loading: true; result?: undefined; status?: undefined; verdict?: undefined; href?: undefined; onOpen?: undefined; moveHref?: undefined; onOpenMove?: undefined; games?: undefined; sanOf?: undefined; onNavigate?: undefined; moveNames?: undefined }
  | {
      loading?: false;
      result?: LastGameResult;
      /** The verdict API's sentence on the stage, the result card's caption; the result's own description without one. */
      verdict?: string;
      /** Why there is no game to draw: the selected game is not scanned, or ended before the stage. */
      status?: StageStatus;
      /** The review address at the stage, for the link's href. */
      href?: string;
      /** Opens it inside the app. */
      onOpen?: () => void;
      /** The review address at one ply of the game, for the score curve's points. */
      moveHref?: (ply: number) => string;
      /** Opens the review at that ply inside the app. */
      onOpenMove?: (ply: number) => void;
      /** Another game's review just before one of its moves (a habit's other games). */
      games?: GameLinks;
      /** The engine's move in SAN from a position; its UCI is shown without it. */
      sanOf?: (fen: Fen, uci: Uci) => San | undefined;
      /** Opens an address inside the app (the "over time" chart's games). */
      onNavigate?: (href: string) => void;
      /** The opening: the book's name of the position after each of the reader's moves it still knows, by ply ("Your moves" tooltips). */
      moveNames?: Readonly<Record<number, string>>;
    }
);

/** Links to any game's review, just before a move of it (1-based ply). */
export interface GameLinks {
  href(reviewPath: string, ply: number): string;
  open(reviewPath: string, ply: number): void;
}

// The dashboard's frame for each card (`useDashboardParts`: the real one
// inside `dashboard.parts`, which the plugin wraps the section in; the
// content alone outside it). The frame is the grid's item itself: the
// section's hide rule counts a composer's frame by the content it holds in
// its body (shared-dashboard's dashboard.css), so it needs no wrapper.
const Frame: React.FC<DashboardCardProps> = (props) => {
  const { Card } = useDashboardParts();
  return <Card {...props} />;
};

export const LastGameSection: React.FC<LastGameSectionProps> = (props) => {
  const { stage } = props;
  const words = STAGE[stage];
  const { Section } = useDashboardParts();
  // The heading alone, while loading or without a game.
  const plainHead = (
    <div className="last-game-head">
      <h2 className="last-game-title">{words.heading}</h2>
    </div>
  );
  if (props.loading) {
    return (
      <section className="last-game" aria-label={words.latest} aria-busy="true">
        <Section head={plainHead}>
          <ResultCard stage={stage} loading />
          <CurveCard stage={stage} loading />
          <GameCard stage={stage} loading />
        </Section>
        {props.history && <HistoryRow stage={stage} loading />}
      </section>
    );
  }
  const { result, status, verdict, href, onOpen, moveHref, onOpenMove, games, sanOf, onNavigate, moveNames } = props;
  const game = result?.game;
  if (!game) {
    return (
      <section className="last-game" aria-label={words.latest}>
        <Section head={plainHead}>
          {/* In an element of its own: a section shows when an item of its body has content. */}
          <div>
            <CardEmpty>{missingText(stage, status, !!result)}</CardEmpty>
          </div>
        </Section>
      </section>
    );
  }
  const heading = stageLabel(stage, game);
  const label = `Review the ${words.name} of your game against ${game.opponent ?? 'your opponent'}, from move ${moveNo(stagePly(game.curve))}`;
  const open = onOpen && href ? onOpen : undefined;
  const head = (
    <div className="last-game-head">
      <div className="last-game-name">
        <h2 className="last-game-title">{words.heading}</h2>
        {[
          ['last-game-label last-game-label-name', heading.name],
          ['last-game-label', heading.detail],
          ['last-game-label', heading.moves],
        ].map(([className, text]) =>
          text ? (
            <span className={className} key={className + text}>
              <span aria-hidden="true">· </span>
              {text}
            </span>
          ) : null,
        )}
      </div>
      {href && (
        <a
          className="last-game-open"
          href={href}
          aria-label={label}
          data-track="game-stage.open"
          data-track-value={stage}
          onClick={(e) => {
            if (!open || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            e.stopPropagation();
            open();
          }}>
          Review the {words.name}
          <svg className="last-game-caret" viewBox="0 0 256 256" aria-hidden="true">
            <path d={CARET_RIGHT} />
          </svg>
        </a>
      )}
    </div>
  );
  return (
    <section
      className={`last-game${open ? ' last-game-link' : ''}`}
      aria-label={words.latest}
      role={open ? 'link' : undefined}
      tabIndex={open ? 0 : undefined}
      title={open ? label : undefined}
      data-track={open ? 'game-stage.open' : undefined}
      data-track-value={open ? stage : undefined}
      onClick={open}
      onKeyDown={
        open
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open();
              }
            }
          : undefined
      }>
      <Section head={head}>
        <ResultCard stage={stage} result={game.result} value={game.value} accuracy={game.accuracy} verdict={verdict} />
        <CurveCard stage={stage} game={game} moveHref={moveHref} onOpenMove={onOpenMove} />
        <GameCard stage={stage} game={game} moveHref={moveHref} onOpenMove={onOpenMove} moveNames={moveNames} />
      </Section>
      {props.history && <HistoryRow stage={stage} result={result!} game={game} games={games} sanOf={sanOf} onNavigate={onNavigate} />}
    </section>
  );
};

// ---------------------------------------------------------------------------
// The stage's result: its classification (the 30-day result cards' names and
// colours), the change in win chance, and a track from lost to winning with
// a hollow dot where the stage started and a solid one where it ended.

const TRACK_W = 320;
/** Cropped to what the track draws (2026-10-10; 44 until then): the dots (r 7 and a 2.5 stroke) centred with a little room. */
const TRACK_H = 30;
const TRACK_PAD = 10;
/** The track's tinted ends: under and over these win chances the reader stands lost or winning. */
const ZONE = 30;

const ResultCard: React.FC<
  | { stage: Phase; loading: true; result?: undefined; value?: undefined; accuracy?: undefined; verdict?: undefined }
  | { stage: Phase; loading?: false; result?: StageResult; value?: number; accuracy?: number; verdict?: string }
> = (props) => {
  const title = `${STAGE[props.stage].name[0].toUpperCase()}${STAGE[props.stage].name.slice(1)} result`;
  const axis = (
    // Inset by the track's own padding, so Lost and Winning sit under their zones.
    <div className="last-game-track-axis" aria-hidden="true" style={{ paddingInline: `${(TRACK_PAD / TRACK_W) * 100}%` }}>
      <span>Lost</span>
      <span>Level</span>
      <span>Winning</span>
    </div>
  );
  if (props.loading) {
    return (
      <Frame
        title={title}
        aside={<span className="last-game-result-label"><TextSkeleton sample="Comeback" /></span>}
        note={<TextSkeleton sample="−3.33 across the middlegame · The middlegame started winning and ended lost." />}
      >
        <div className="last-game-result-figures">
          <span className="last-game-result-figure">
            <span className="last-game-result-value"><TextSkeleton sample="80" /></span>
            <span className="last-game-result-caption">accuracy</span>
          </span>
          <span className="last-game-result-figure">
            <span className="last-game-result-value"><TextSkeleton sample="+0.00" /></span>
            <span className="last-game-result-caption">score</span>
          </span>
        </div>
        <SkeletonPlot width={TRACK_W} height={TRACK_H} label="Loading the result" />
        {axis}
      </Frame>
    );
  }
  const { result, value, accuracy, verdict } = props;
  if (!result) {
    return (
      <Frame title={title}>
        <CardEmpty>No score for this stage.</CardEmpty>
      </Frame>
    );
  }
  // The classification at the right of the title in the result's colour;
  // under the title the reader's accuracy over the stage at the left and
  // the score change in pawns at the right, each over its caption, as
  // Home's stage tiles (2026-10-10; on one headline until then; accuracy
  // first wherever both show; win chance until 2026-10-08); "—" for a
  // stage with too few of the reader's moves to rate, or no score. Under
  // the track the verdict API's sentence (the result's own description
  // until the API answers).
  const delta = Math.round(result.end) - Math.round(result.start);
  const note = verdict ?? result.description;
  const x = (pct: number) => TRACK_PAD + (pct / 100) * (TRACK_W - 2 * TRACK_PAD);
  const mid = TRACK_H / 2;
  const from = Math.min(x(result.start), x(result.end));
  const to = Math.max(x(result.start), x(result.end));
  const tone = delta > 0 ? 'gain' : delta < 0 ? 'loss' : 'even';
  const words = `${result.label}: win chance ${Math.round(result.start)}% at the start, ${Math.round(result.end)}% at the end (${signedInt(delta)}%)${accuracy === undefined ? '' : `, accuracy ${Math.round(accuracy)}`}${value === undefined ? '' : `, ${signed(value)} pawns`}. ${result.description}`;
  return (
    <Frame title={title} aside={<span className="last-game-result-label" style={{ color: result.colour }}>{result.label}</span>} note={note}>
      <div className="last-game-result-figures">
        <span className="last-game-result-figure" title={accuracy === undefined ? 'Too few of your moves in this stage to rate' : 'Your accuracy over this stage'}>
          <span className="last-game-result-value">{accuracy === undefined ? '—' : Math.round(accuracy)}</span>
          <span className="last-game-result-caption">accuracy</span>
        </span>
        <span className="last-game-result-figure" title={`${signedInt(delta)}% win chance`}>
          <span className={`last-game-result-value${value === undefined ? '' : scoreTone(value)}`}>{value === undefined ? '—' : signed(value)}</span>
          <span className="last-game-result-caption">score</span>
        </span>
      </div>
      <svg className="last-game-track" viewBox={`0 0 ${TRACK_W} ${TRACK_H}`} role="img" aria-label={words}>
        <title>{words}</title>
        <rect x={x(0)} y={mid - 2} width={x(100) - x(0)} height={4} rx={2} className="last-game-track-rail" />
        <rect x={x(0)} y={mid - 2} width={x(ZONE) - x(0)} height={4} rx={2} className="last-game-track-lost" />
        <rect x={x(100 - ZONE)} y={mid - 2} width={x(100) - x(100 - ZONE)} height={4} rx={2} className="last-game-track-winning" />
        {to - from > 1 && <rect x={from} y={mid - 4} width={to - from} height={8} rx={4} className={`last-game-track-bar last-game-track-${tone}`} style={{ fill: result.colour }} />}
        <circle cx={x(result.start)} cy={mid} r={7} className="last-game-track-start" style={{ stroke: result.colour }} />
        <circle cx={x(result.end)} cy={mid} r={7} className="last-game-track-end" style={{ fill: result.colour }} />
      </svg>
      {axis}
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// The score through the stage, ply by ply, the reader's blunders and misses marked.

const W = 320;
const H = 120;
const PAD = { left: 30, right: 8, top: 8, bottom: 18 };

type CurveProps =
  | { stage: Phase; loading: true; game?: undefined; moveHref?: undefined; onOpenMove?: undefined }
  | { stage: Phase; loading?: false; game: LastGame; moveHref?: (ply: number) => string; onOpenMove?: (ply: number) => void };

const CurveCard: React.FC<CurveProps> = (props) => {
  const title = `Score through the ${STAGE[props.stage].name}`;
  // The ply under the pointer (or chosen with the arrow keys), shown as a point with its score.
  const [hover, setHover] = React.useState<number | undefined>(undefined);
  if (props.loading) {
    return (
      <Frame title={title}>
        <SkeletonPlot width={W} height={H} label="Loading the score" />
        <MarksLegend />
      </Frame>
    );
  }
  const { curve } = props.game;
  const points = curve.scores.map((s, i) => ({ ply: curve.fromPly + i, s }));
  const known = points.filter((p): p is { ply: number; s: number } => p.s !== null);
  const max = Math.max(1, ...known.map((p) => Math.abs(p.s)));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const span = Math.max(1, curve.toPly - curve.fromPly);
  const x = (ply: number) => PAD.left + ((ply - curve.fromPly) / span) * plotW;
  const y = (s: number) => PAD.top + plotH / 2 - (s / max) * (plotH / 2);
  const line = known.map((p, i) => `${i ? 'L' : 'M'} ${x(p.ply).toFixed(1)} ${y(p.s).toFixed(1)}`).join(' ');
  const blunders = curve.marks.filter((m) => m.kind === 'blunder').length;
  const misses = curve.marks.length - blunders;
  const counts = curve.marks.length ? [blunders && plural(blunders, 'blunder'), misses && plural(misses, 'miss', 'misses')].filter(Boolean).join(' · ') : 'No blunders or misses';
  const scoreAt = new Map(known.map((p) => [p.ply, p.s]));
  const sanAt = new Map(curve.marks.map((m) => [m.ply, m.san]));
  const { moveHref, onOpenMove } = props;
  const exit = props.game.bookExit;
  const clickable = !!(moveHref && onOpenMove);
  // The scored ply nearest to a horizontal position in the chart's own units.
  const nearest = (vx: number) => {
    let best: number | undefined;
    for (const p of known) if (best === undefined || Math.abs(x(p.ply) - vx) < Math.abs(x(best) - vx)) best = p.ply;
    return best;
  };
  const toChart = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return ((e.clientX - box.left) / box.width) * W;
  };
  const step = (by: number) => {
    const i = known.findIndex((p) => p.ply === hover);
    const next = known[Math.min(known.length - 1, Math.max(0, i < 0 ? (by > 0 ? 0 : known.length - 1) : i + by))];
    setHover(next?.ply);
  };
  const open = (ply: number) => onOpenMove?.(ply);
  const point = hover !== undefined ? scoreAt.get(hover) : undefined;
  const clockAt = hover !== undefined ? curve.clock?.[hover - curve.fromPly] : undefined;
  const tip = hover !== undefined && point !== undefined ? `${moveNo(hover)}${sanAt.get(hover) ?? ''} ${signed(point)}${clockAt != null ? ` · ⏱ ${clockText(clockAt)}` : ''}` : '';
  const tipW = tip.length * 5.6 + 10;
  const tipX = hover !== undefined ? Math.min(W - PAD.right - tipW, Math.max(PAD.left, x(hover) - tipW / 2)) : 0;
  const tipY = point !== undefined && y(point) > PAD.top + 22 ? y(point) - 22 : (point !== undefined ? y(point) + 8 : 0);
  return (
    <Frame title={title}>
      <svg
        className={`last-game-curve${clickable ? ' last-game-curve-link' : ''}`}
        viewBox={`0 0 ${W} ${H}`}
        role={clickable ? 'slider' : 'img'}
        aria-label={`${title}: from ${signed(known[0]?.s ?? 0)} to ${signed(known[known.length - 1]?.s ?? 0)}; ${counts}${clickable ? '. Arrow keys pick a move, Enter opens it in the review.' : ''}`}
        aria-valuemin={clickable ? curve.fromPly : undefined}
        aria-valuemax={clickable ? curve.toPly : undefined}
        aria-valuenow={clickable ? hover : undefined}
        aria-valuetext={clickable && tip ? `Move ${tip}` : undefined}
        tabIndex={clickable ? 0 : undefined}
        data-track={clickable ? 'game-stage.curve' : undefined}
        data-track-value={clickable ? props.stage : undefined}
        onPointerMove={(e) => setHover(nearest(toChart(e)))}
        onPointerLeave={() => setHover(undefined)}
        onBlur={() => setHover(undefined)}
        onClick={
          clickable
            ? (e) => {
                // The section around is a link to the stage; a point opens its own move instead.
                e.stopPropagation();
                const ply = nearest(toChart(e as unknown as React.PointerEvent<SVGSVGElement>));
                if (ply !== undefined) open(ply);
              }
            : undefined
        }
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  e.stopPropagation();
                  step(e.key === 'ArrowRight' ? 1 : -1);
                } else if ((e.key === 'Enter' || e.key === ' ') && hover !== undefined) {
                  e.preventDefault();
                  e.stopPropagation();
                  open(hover);
                }
              }
            : undefined
        }>
        <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--viz-grid)" strokeWidth={1} />
        <text x={PAD.left - 4} y={PAD.top + 4} textAnchor="end" className="last-game-axis">{`+${max.toFixed(0)}`}</text>
        <text x={PAD.left - 4} y={y(0) + 4} textAnchor="end" className="last-game-axis">0</text>
        <text x={PAD.left - 4} y={H - PAD.bottom + 2} textAnchor="end" className="last-game-axis">{`−${max.toFixed(0)}`}</text>
        <text x={PAD.left} y={H - 3} className="last-game-axis">{`move ${Math.ceil(Math.max(1, curve.fromPly + 1) / 2)}`}</text>
        <text x={W - PAD.right} y={H - 3} textAnchor="end" className="last-game-axis">{`move ${Math.ceil(curve.toPly / 2)}`}</text>
        {line && <path d={line} fill="none" stroke="var(--viz-ink)" strokeWidth={1.5} strokeLinejoin="round" />}
        {curve.marks.map((m) => {
          const s = scoreAt.get(m.ply);
          if (s === undefined) return null;
          return (
            <circle key={m.ply} cx={x(m.ply)} cy={y(s)} r={4} className={m.kind === 'blunder' ? 'last-game-mark-blunder' : 'last-game-mark-miss'}>
              <title>{`${moveNo(m.ply)}${m.san}: ${m.kind}, ${signed(s)} after it`}</title>
            </circle>
          );
        })}
        {exit && exit.ply > curve.fromPly && exit.ply <= curve.toPly && (
          <g className="last-game-book-exit" pointerEvents="none">
            <line x1={x(exit.ply - 0.5)} x2={x(exit.ply - 0.5)} y1={PAD.top} y2={H - PAD.bottom} />
            {/* The label reads away from the nearer edge, so it is never cut off. */}
            <text x={x(exit.ply - 0.5) + (x(exit.ply - 0.5) > W / 2 ? -3 : 3)} y={PAD.top + 8} textAnchor={x(exit.ply - 0.5) > W / 2 ? 'end' : 'start'}>
              {`left the book · ${exit.by === props.game.colour ? 'you' : 'opponent'}`}
            </text>
          </g>
        )}
        {hover !== undefined && point !== undefined && (
          <g className="last-game-hover" pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} className="last-game-hover-rule" />
            <circle cx={x(hover)} cy={y(point)} r={4.5} className="last-game-hover-point" />
            <rect x={tipX} y={tipY} width={tipW} height={15} rx={3} className="last-game-hover-tip" />
            <text x={tipX + tipW / 2} y={tipY + 11} textAnchor="middle" className="last-game-hover-text">{tip}</text>
          </g>
        )}
      </svg>
      <ClockStrip curve={curve} x={x} lostOnTime={lostOnTime(props.game, props.stage)} />
      <MarksLegend blunders={blunders} misses={misses} />
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// The reader's clock through the stage (2026-10-10), under the score chart on
// the same x scale: a band from the stage's start to its end whose height is
// the time left against the stage's starting time, so it thins as the clock
// runs down, muted while a minute or more remains and in the loss colour
// under it (`TIME_SLIP_SECONDS`, the app's time-pressure line); the start
// and end times at either side, a "1:00" tick where the clock first goes
// under a minute, and "0:00 · lost on time" at the right when the game was
// lost on time in this stage. Nothing without clocks.

const STRIP_H = 12;

/** Seconds as "m:ss" ("h:mm:ss" from an hour), whole seconds. */
function clockText(seconds: number): string {
  const t = Math.max(0, Math.floor(seconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const ss = String(t % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

const STAGE_ORDER: readonly Phase[] = ['opening', 'middlegame', 'ending'];

/** The game was lost on time and this is its last stage. */
function lostOnTime(game: LastGame, stage: Phase): boolean {
  if (game.facts.ending !== 'time' || game.outcome !== 'loss') return false;
  const curves = game.facts.stageCurves;
  return !curves || !STAGE_ORDER.slice(STAGE_ORDER.indexOf(stage) + 1).some((s) => curves[s]);
}

const ClockStrip: React.FC<{ curve: LastGame['curve']; x: (ply: number) => number; lostOnTime: boolean }> = ({ curve, x, lostOnTime }) => {
  const clock = curve.clock;
  const known = clock?.filter((c): c is number => c !== null) ?? [];
  if (!clock || !known.length) return null;
  const start = known[0];
  const end = known[known.length - 1];
  const ref = Math.max(1, start);
  const height = (c: number) => Math.min(STRIP_H, Math.max(c > 0 ? 1 : 0, (c / ref) * STRIP_H));
  // One step per ply: the time left after the move made between two positions.
  const steps: Array<{ x0: number; x1: number; c: number }> = [];
  if (clock.length === 1) steps.push({ x0: x(curve.fromPly), x1: x(curve.toPly + 1), c: start });
  for (let i = 1; i < clock.length; i++) {
    const c = clock[i];
    if (c !== null) steps.push({ x0: x(curve.fromPly + i - 1), x1: x(curve.fromPly + i), c });
  }
  // Where the clock first goes under a minute, when it starts the stage with more.
  const under = start >= TIME_SLIP_SECONDS ? clock.findIndex((c) => c !== null && c < TIME_SLIP_SECONDS) : -1;
  const tickX = under > 0 ? x(curve.fromPly + under - 1) : undefined;
  const pct = (vx: number) => (vx / W) * 100;
  const tickPct = tickX !== undefined ? pct(tickX) : undefined;
  const tickLabel = tickPct !== undefined && tickPct > 22 && tickPct < (lostOnTime ? 58 : 78);
  const endText = lostOnTime ? '0:00 · lost on time' : clockText(end);
  return (
    <div className="last-game-clock">
      <svg
        className="last-game-clock-band"
        viewBox={`0 0 ${W} ${STRIP_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Your clock through the stage: ${clockText(start)} at the start, ${lostOnTime ? 'lost on time' : `${clockText(end)} at the end`}`}>
        {steps.map((st, i) => (
          <rect
            key={i}
            x={st.x0}
            width={Math.max(0, st.x1 - st.x0) + 0.2}
            y={STRIP_H - height(st.c)}
            height={height(st.c)}
            className={st.c < TIME_SLIP_SECONDS ? 'last-game-clock-low' : 'last-game-clock-ok'}
          />
        ))}
        {tickX !== undefined && <line x1={tickX} x2={tickX} y1={0} y2={STRIP_H} className="last-game-clock-tick" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="last-game-clock-labels" aria-hidden="true" style={{ paddingLeft: `${pct(PAD.left)}%`, paddingRight: `${pct(PAD.right)}%` }}>
        <span>{`⏱ ${clockText(start)}`}</span>
        {tickLabel && <span className="last-game-clock-tick-label" style={{ left: `${tickPct}%` }}>1:00</span>}
        <span className={lostOnTime ? 'last-game-clock-lost' : undefined}>{endText}</span>
      </div>
    </div>
  );
};

/**
 * The reader's moves in the opening as a strip of squares, one per move,
 * coloured by the app's grade (`MoveGrade`, `--viz-grade-*`), with a rule
 * where the game left the book. A square opens the review just before its
 * move, where the board draws the move played and the engine's. Under it,
 * the key to the grades the strip shows.
 * 2026-10-10: under each square the move in SAN, a "book" caption under
 * the exit rule, and while the book still knows the position the square's
 * tooltip names the variation there ("3.Bc4 · Italian Game"; `names`, by
 * ply), the grade otherwise. No score change under the move: the grade's
 * colour is enough (2026-10-10).
 */
const MoveStrip: React.FC<{
  moves: GradedMove[];
  exit?: { ply: number; by: Color };
  colour: Color;
  moveHref?: (ply: number) => string;
  onOpenMove?: (ply: number) => void;
  /** The book's name of the position after each move it still knows, by ply. */
  names?: Readonly<Record<number, string>>;
}> = ({ moves, exit, colour, moveHref, onOpenMove, names }) => {
  const shown = MOVE_GRADES.filter((g) => moves.some((m) => m.grade === g));
  // The rule stands before the first of the reader's moves after the book was left.
  const exitBefore = exit ? moves.findIndex((m) => m.ply >= exit.ply) : -1;
  const who = exit && (exit.by === colour ? 'you' : 'your opponent');
  return (
    <div className="last-game-strip">
      <div className="last-game-strip-moves" role="list" aria-label="Your moves in the opening">
        {moves.map((m, i) => {
          const name = names?.[m.ply];
          const title = `${moveNo(m.ply)}${m.san}${name ? ` · ${name}` : `: ${gradeName(m.grade)}`}${onOpenMove ? ' · opens the review at this move' : ''}`;
          const square = moveHref && onOpenMove ? (
            <a
              className={`last-game-strip-move grade-fill-${m.grade}`}
              href={moveHref(m.ply)}
              title={title}
              aria-label={title}
              data-track="game-stage.move"
              data-track-value="opening"
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                e.stopPropagation();
                onOpenMove(m.ply);
              }}>
              {Math.ceil(m.ply / 2)}
            </a>
          ) : (
            <span className={`last-game-strip-move grade-fill-${m.grade}`} title={title}>
              {Math.ceil(m.ply / 2)}
            </span>
          );
          return (
            <React.Fragment key={m.ply}>
              {i === exitBefore && (
                <span className="last-game-strip-book" title={`Left the book here: ${who}`} aria-label={`left the book: ${who}`}>
                  <span className="last-game-strip-exit" />
                  <span className="last-game-strip-book-label" aria-hidden="true">book</span>
                </span>
              )}
              <span role="listitem" className="last-game-strip-item">
                {square}
                <span className="last-game-strip-san">{m.san}</span>
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <MoveGradeLegend grades={shown} />
    </div>
  );
};

/** The marks' key, each with its count in the last game; skeletons for the counts while loading. */
const MarksLegend: React.FC<{ blunders?: number; misses?: number }> = ({ blunders, misses }) => (
  <ul className="legend-row last-game-marks-legend">
    <li>
      <span className="legend-swatch last-game-mark-blunder" /> Blunders
      <span className="last-game-marks-count">{blunders ?? <TextSkeleton sample="0" />}</span>
    </li>
    <li>
      <span className="legend-swatch last-game-mark-miss" /> Misses
      <span className="last-game-marks-count">{misses ?? <TextSkeleton sample="0" />}</span>
    </li>
  </ul>
);

// ---------------------------------------------------------------------------
// Row one, the game: the stage's own card about this game alone.

type GameCardProps =
  | { stage: Phase; loading: true; game?: undefined; moveHref?: undefined; onOpenMove?: undefined; moveNames?: undefined }
  | { stage: Phase; loading?: false; game: LastGame; moveHref?: (ply: number) => string; onOpenMove?: (ply: number) => void; moveNames?: Readonly<Record<number, string>> };

const GameCard: React.FC<GameCardProps> = (props) => {
  switch (props.stage) {
    case 'opening':
      return props.loading ? <MovesCard loading /> : <MovesCard game={props.game} moveHref={props.moveHref} onOpenMove={props.onOpenMove} names={props.moveNames} />;
    case 'middlegame':
      return props.loading ? <CauseCard loading /> : <CauseCard game={props.game} moveHref={props.moveHref} onOpenMove={props.onOpenMove} />;
    case 'ending':
      return props.loading ? <EndingCard loading /> : <EndingCard game={props.game} />;
  }
};

// The opening's moves of the reader's, graded, with where the book was left.
const MovesCard: React.FC<
  | { loading: true; game?: undefined; moveHref?: undefined; onOpenMove?: undefined; names?: undefined }
  | { loading?: false; game: LastGame; moveHref?: (ply: number) => string; onOpenMove?: (ply: number) => void; names?: Readonly<Record<number, string>> }
> = (props) => {
  const title = 'Your moves';
  if (props.loading) {
    return (
      <Frame title={title} note={<TextSkeleton sample="6 moves · a square opens the review at the move" />}>
        <SkeletonRows labels={[undefined, undefined]} value="0" />
      </Frame>
    );
  }
  const { game, moveHref, onOpenMove, names } = props;
  const moves = game.moves ?? [];
  if (!moves.length) {
    return (
      <Frame title={title}>
        <CardEmpty>No move of yours in the opening.</CardEmpty>
      </Frame>
    );
  }
  return (
    <Frame title={title} aside="against the engine" note={`${plural(moves.length, 'move')}${onOpenMove ? ' · a square opens the review at the move' : ''}`}>
      <MoveStrip moves={moves} exit={game.bookExit} colour={game.colour} moveHref={moveHref} onOpenMove={onOpenMove} names={names} />
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Row two, the game against the reader's history (the stage screens): the
// stage's measure against the average, the stage's own comparison, and the
// measure over time.

const HistoryRow: React.FC<
  | { stage: Phase; loading: true; result?: undefined; game?: undefined; games?: undefined; sanOf?: undefined; onNavigate?: undefined }
  | {
      stage: Phase;
      loading?: false;
      result: LastGameResult;
      game: LastGame;
      games?: GameLinks;
      sanOf?: (fen: Fen, uci: Uci) => San | undefined;
      onNavigate?: (href: string) => void;
    }
> = (props) => {
  const { stage } = props;
  const { Section } = useDashboardParts();
  return (
    <div className="last-game-history">
      <Section label="Against your history">
        {props.loading ? (
          <>
            <AverageCard stage={stage} loading />
            <Frame title={STAGE_HISTORY_TITLE[stage]}>
              <SkeletonRows labels={[undefined, undefined, undefined]} value="0" />
            </Frame>
            <TrendCard stage={stage} loading />
          </>
        ) : (
          <>
            <AverageCard stage={stage} game={props.game} average={props.result.average} averageOf={props.result.averageOf} />
            {stage === 'opening' &&
              (props.game.habit ? <HabitCard game={props.game} habit={props.game.habit} games={props.games} sanOf={props.sanOf} /> : <NoHabitCard />)}
            {stage === 'middlegame' && <UsualCauseCard game={props.game} causes={props.result.causes} />}
            {stage === 'ending' && <TypeRecordCard game={props.game} typeRecord={props.result.typeRecord} />}
            <TrendCard stage={stage} game={props.game} points={props.result.history ?? []} onNavigate={props.onNavigate} />
          </>
        )}
      </Section>
    </div>
  );
};

const STAGE_HISTORY_TITLE: Record<Phase, string> = { opening: "You've played this before", middlegame: 'Your usual cause', ending: 'Your record in this ending' };

// The opening when no move of this game repeats a mistake from the others.
const NoHabitCard: React.FC = () => (
  <Frame title={STAGE_HISTORY_TITLE.opening}>
    <p className="last-game-third-head last-game-third-fine">No repeated mistake</p>
    <CardEmpty>No move of this opening is one you got wrong in the same position in another game of the last 30 days.</CardEmpty>
  </Frame>
);

// The middlegame: the period's middlegames gone wrong by cause, this game's
// cause marked; only the causes that happened, and this game's (2026-10-10).
const UsualCauseCard: React.FC<{ game: LastGame; causes?: Partial<Record<GroundCause, number>> }> = ({ game, causes }) => {
  const total = GROUND_CAUSE_VIEWS.reduce((n, v) => n + (causes?.[v.cause] ?? 0), 0);
  const mine = game.middlegame?.cause;
  const rows = shownRows(GROUND_CAUSE_VIEWS, (v) => causes?.[v.cause] ?? 0, (v) => v.cause === mine);
  const max = Math.max(1, ...rows.map((v) => causes?.[v.cause] ?? 0));
  return (
    <Frame title={STAGE_HISTORY_TITLE.middlegame} note={total ? `${plural(total, 'middlegame')} gone wrong in the last 30 days` : 'No middlegame gone wrong in the last 30 days'}>
      <p className="last-game-third-head">{mine ? `This game: ${groundCauseView(mine).label}` : <span className="last-game-third-fine">This middlegame held</span>}</p>
      <div className="split-rows">
        {rows.map((v) => {
          const n = causes?.[v.cause] ?? 0;
          return (
            <div className={`split-row${v.cause === mine ? ' last-game-row-mine' : ''}`} key={v.cause} title={`${v.description}${v.cause === mine ? ' · this game' : ''}`}>
              <span className="split-label">{v.label}</span>
              <span className="split-bar" style={{ width: `${(100 * n) / max}%` }}>
                {n > 0 && <span className="split-segment" style={{ flex: 1, background: v.colour }} />}
              </span>
              <span className="split-total">{n}</span>
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// The ending: the period's record in the same type of ending, this game's
// result marked and named in its colour above the rows, as the middlegame's
// usual cause names this game's (2026-10-10; the type before, now the
// section heading's). Only the results that happened are listed, and this
// game's (2026-10-10).
const TypeRecordCard: React.FC<{ game: LastGame; typeRecord?: TypeRecord }> = ({ game, typeRecord }) => {
  const mine = game.ending?.result;
  const mineView = mine ? endingResultView(mine) : undefined;
  const total = ENDING_RESULT_VIEWS.reduce((n, v) => n + (typeRecord?.[v.result] ?? 0), 0);
  const rows = shownRows(ENDING_RESULT_VIEWS, (v) => typeRecord?.[v.result] ?? 0, (v) => v.result === mine);
  const max = Math.max(1, ...rows.map((v) => typeRecord?.[v.result] ?? 0));
  return (
    <Frame title={STAGE_HISTORY_TITLE.ending} note={total ? `${plural(total, 'ending')} of this kind in the last 30 days, this one included` : 'No ending of this kind in the last 30 days'}>
      {mineView && (
        <p className="last-game-third-head" title={mineView.description}>
          <span style={{ color: mineView.colour }}>This game: {mineView.label}</span>
        </p>
      )}
      <div className="split-rows">
        {rows.map((v) => {
          const n = typeRecord?.[v.result] ?? 0;
          return (
            <div className={`split-row${v.result === mine ? ' last-game-row-mine' : ''}`} key={v.result} title={`${v.description}${v.result === mine ? ' · this game' : ''}`}>
              <span className="split-label">{v.label}</span>
              <span className="split-bar" style={{ width: `${(100 * n) / max}%` }}>
                {n > 0 && <span className="split-segment" style={{ flex: 1, background: v.colour }} />}
              </span>
              <span className="split-total">{n}</span>
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

/** Pawns either way where the "over time" scale stops. */
const TREND_CAP = 5;

// The stage's measure game by game over the period, a dot each (the result's
// colour), this game ringed; a dot opens that game's review at the stage.
const TrendCard: React.FC<
  | { stage: Phase; loading: true; game?: undefined; points?: undefined; onNavigate?: undefined }
  | { stage: Phase; loading?: false; game: LastGame; points: HistoryPoint[]; onNavigate?: (href: string) => void }
> = (props) => {
  const title = 'Over time';
  const aside = props.stage === 'opening' ? 'score after the opening' : 'score change';
  if (props.loading) {
    return (
      <Frame title={title} aside={aside}>
        <SkeletonPlot width={W} height={H} label="Loading the games" />
      </Frame>
    );
  }
  const { stage, game, points, onNavigate } = props;
  if (points.length < 2) {
    return (
      <Frame title={title} aside={aside}>
        <CardEmpty>{stage === 'opening' ? `No other game of the ${game.opening ?? 'same opening'} in the last 30 days.` : 'No other game to set this one against yet.'}</CardEmpty>
      </Frame>
    );
  }
  const from = points[0].playedAt;
  const to = points[points.length - 1].playedAt;
  // A few large swings would flatten the rest: the scale stops at TREND_CAP pawns and larger values sit on its edge.
  const max = Math.min(TREND_CAP, Math.max(1, ...points.map((p) => Math.abs(p.value))));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + (to > from ? ((t - from) / (to - from)) * plotW : plotW / 2);
  const y = (v: number) => PAD.top + plotH / 2 - (Math.max(-max, Math.min(max, v)) / max) * (plotH / 2);
  const over = stage === 'opening' ? `${plural(points.length, 'game')} of the ${game.opening ?? 'opening'}` : plural(points.length, 'game');
  return (
    <Frame title={title} aside={aside} note={`${over} · a dot opens that game at the ${STAGE[stage].name}`}>
      <svg className="last-game-trend" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${over}, this game ${signed(game.value ?? 0)}`}>
        <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--viz-grid)" strokeWidth={1} />
        <text x={PAD.left - 4} y={PAD.top + 4} textAnchor="end" className="last-game-axis">{`+${max.toFixed(0)}`}</text>
        <text x={PAD.left - 4} y={y(0) + 4} textAnchor="end" className="last-game-axis">0</text>
        <text x={PAD.left - 4} y={H - PAD.bottom + 2} textAnchor="end" className="last-game-axis">{`−${max.toFixed(0)}`}</text>
        <text x={PAD.left} y={H - 3} className="last-game-axis">{date(from)}</text>
        <text x={W - PAD.right} y={H - 3} textAnchor="end" className="last-game-axis">{date(to)}</text>
        {points.map((p) => {
          const go = p.href && onNavigate ? () => onNavigate(p.href!) : undefined;
          return (
            <g
              key={p.id}
              className={`last-game-trend-dot last-game-trend-${p.outcome}${p.current ? ' last-game-trend-current' : ''}${go ? ' last-game-trend-link' : ''}`}
              data-track={go ? 'game-stage.trend-dot' : undefined}
              data-track-value={go ? stage : undefined}
              onClick={
                go
                  ? (e) => {
                      e.stopPropagation();
                      go();
                    }
                  : undefined
              }>
              <title>{`${date(p.playedAt)} · ${OUTCOME[p.outcome]}${p.current ? ' (this game)' : ''}: ${signed(p.value)}`}</title>
              <circle cx={x(p.playedAt)} cy={y(p.value)} r={p.current ? 5 : 3.5} />
            </g>
          );
        })}
      </svg>
    </Frame>
  );
};

/** A move as a link into the review, or plain text without one. */
const MoveLink: React.FC<{ ply: number; san: string; moveHref?: (ply: number) => string; onOpenMove?: (ply: number) => void }> = ({ ply, san, moveHref, onOpenMove }) => {
  const text = `${moveNo(ply)}${san}`;
  if (!moveHref || !onOpenMove) return <b>{text}</b>;
  return (
    <a
      className="last-game-move"
      href={moveHref(ply)}
      data-track="game-stage.move"
      data-track-value="middlegame"
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        e.stopPropagation();
        onOpenMove(ply);
      }}>
      {text}
    </a>
  );
};

// The stage's measure for this game against the reader's average: the
// opening's score after it against the same family's games; a later stage's
// score change against every game that reached it.
const AVERAGE_TITLE: Record<Phase, string> = { opening: 'Your opening', middlegame: 'Your middlegame', ending: 'Your ending' };
const AverageCard: React.FC<
  { stage: Phase } & ({ loading: true; game?: undefined; average?: undefined; averageOf?: undefined } | { loading?: false; game: LastGame; average?: number; averageOf: number })
> = (props) => {
  const title = AVERAGE_TITLE[props.stage];
  const aside = props.stage === 'opening' ? 'score after the opening' : 'score change';
  if (props.loading) {
    return (
      <Frame title={title} aside={aside} note={<TextSkeleton sample="Average over 12 games of the Sicilian Defense" />}>
        <p className="last-game-third-head">{props.stage === 'opening' ? 'Score after the opening' : `Score change across the ${STAGE[props.stage].name}`}</p>
        <SkeletonRows labels={['This game', 'Your average']} value="+0.00" />
      </Frame>
    );
  }
  const { stage, game, average, averageOf } = props;
  const over =
    stage === 'opening' ? (game.opening ? `${plural(averageOf, 'game')} of the ${game.opening}` : plural(averageOf, 'game')) : `${plural(averageOf, 'game')} that reached the ${STAGE[stage].name}`;
  // What the card compares; the opening's name is the section heading's (2026-10-10).
  const head = stage === 'opening' ? 'Score after the opening' : `Score change across the ${STAGE[stage].name}`;
  return (
    <Frame title={title} aside={aside} note={averageOf ? `Average over ${over}` : 'No average yet'}>
      <p className="last-game-third-head">{head}</p>
      <DivergingBars
        rows={[
          { key: 'game', label: 'This game', value: game.value },
          { key: 'average', label: 'Your average', value: average, detail: over },
        ]}
      />
    </Frame>
  );
};

// The opening, when this game repeats a habit: the move played in a position
// the reader has got wrong in other games too, the engine's move there, and
// a bar per game for the pawns given up, oldest first, this game's marked.
// A bar opens that game's review just before the move; so does the move.
const HabitCard: React.FC<{ game: LastGame; habit: GameHabit; games?: GameLinks; sanOf?: (fen: Fen, uci: Uci) => San | undefined }> = ({ game, habit, games, sanOf }) => {
  const total = habit.games.reduce((n, g) => n + g.loss, 0);
  const average = total / habit.games.length;
  const best = sanOf?.(habit.fen, habit.best) ?? habit.best;
  const max = Math.max(0.5, ...habit.games.map((g) => g.loss));
  const BW = 320;
  const BH = 64;
  const slot = BW / habit.games.length;
  const barW = Math.min(28, slot * 0.7);
  const date = (t: number) => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const open = (path: string | undefined, ply: number) => (path && games ? () => games.open(path, ply) : undefined);
  const nth = habit.games.length;
  const ord = nth % 10 === 1 && nth % 100 !== 11 ? 'st' : nth % 10 === 2 && nth % 100 !== 12 ? 'nd' : nth % 10 === 3 && nth % 100 !== 13 ? 'rd' : 'th';
  const self = game.reviewPath;
  return (
    <Frame title="You've played this before" note={`pawns lost each game: ${signed(-average)} on average · ${signed(-total)} in all · a bar opens that game at the move`}>
      <p className="last-game-third-head last-game-habit-line">
        {game.variation ?? game.opening ?? 'Opening'} · after {lineTail(habit.line) || 'the start'}
      </p>
      <p className="last-game-habit-move">
        {self && games ? (
          <a
            className="last-game-habit-played"
            href={games.href(self, habit.ply)}
            data-track="game-stage.habit-move"
            data-track-value="opening"
            onClick={(e) => {
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              e.stopPropagation();
              games.open(self, habit.ply);
            }}>
            {numberedMove(habit.ply, habit.san)}
          </a>
        ) : (
          <b className="last-game-habit-played">{numberedMove(habit.ply, habit.san)}</b>
        )}
        <span className="dash-muted"> — {nth}{ord} time here · engine </span>
        <b className="last-game-habit-best">{numberedMove(habit.ply, best)}</b>
      </p>
      <svg className="last-game-habit-bars" viewBox={`0 0 ${BW} ${BH + 14}`} role="img" aria-label={`Pawns lost in this position in each of ${plural(nth, 'game')}, oldest first`}>
        <line x1={0} x2={BW} y1={1} y2={1} stroke="var(--viz-grid)" strokeWidth={1} />
        {habit.games.map((g, i) => {
          const h = Math.max(2, (g.loss / max) * (BH - 4));
          const x = i * slot + (slot - barW) / 2;
          const go = open(g.reviewPath, g.ply);
          return (
            <g
              key={g.id}
              className={`last-game-habit-bar${g.current ? ' last-game-habit-current' : ''}${go ? ' last-game-habit-link' : ''}`}
              data-track={go ? 'game-stage.habit-game' : undefined}
              data-track-value={go ? 'opening' : undefined}
              onClick={
                go
                  ? (e) => {
                      e.stopPropagation();
                      go();
                    }
                  : undefined
              }>
              <title>{`${date(g.playedAt)}${g.current ? ' (this game)' : ''}: ${numberedMove(g.ply, g.san)}, ${signed(-g.loss)}${go ? ' · opens that game at the move' : ''}`}</title>
              <rect x={x} y={1} width={barW} height={h} rx={2} />
              <text x={x + barW / 2} y={BH + 12} textAnchor="middle" className="last-game-axis">
                {g.current ? 'this' : date(g.playedAt)}
              </text>
            </g>
          );
        })}
      </svg>
    </Frame>
  );
};

// The middlegame: why it went wrong (the game's cause and the move to
// practise) and the win chance lost to each cause. The period's usual cause
// is the second row's (`UsualCauseCard`).
const CauseCard: React.FC<
  | { loading: true; game?: undefined; moveHref?: undefined; onOpenMove?: undefined }
  | { loading?: false; game: LastGame; moveHref?: (ply: number) => string; onOpenMove?: (ply: number) => void }
> = (props) => {
  const title = 'Where it went wrong';
  if (props.loading) {
    return (
      <Frame title={title} aside="win chance lost">
        <p className="last-game-third-head">
          <TextSkeleton sample="Tactic" />
        </p>
        <SkeletonRows labels={GROUND_CAUSE_VIEWS.map((v) => v.label)} value="−12%" />
      </Frame>
    );
  }
  const { game, moveHref, onOpenMove } = props;
  const detail = game.middlegame;
  const costs = detail?.costs;
  const max = Math.max(1, ...GROUND_CAUSE_VIEWS.map((v) => costs?.[v.cause] ?? 0));
  const cause = detail?.cause ? groundCauseView(detail.cause) : undefined;
  const practice = detail?.practice;
  return (
    <Frame title={title} aside="win chance lost" note={practice && onOpenMove ? 'the move opens the review at it' : undefined}>
      <p className="last-game-third-head" style={cause ? { color: cause.colour } : undefined} title={cause?.description}>
        {cause ? (
          <>
            {cause.label}
            {practice && (
              <span className="last-game-third-move">
                {' '}
                at <MoveLink ply={practice.ply} san={practice.san} moveHref={moveHref} onOpenMove={onOpenMove} />
              </span>
            )}
          </>
        ) : (
          <span className="last-game-third-fine">The middlegame held</span>
        )}
      </p>
      <div className="split-rows">
        {GROUND_CAUSE_VIEWS.map((v) => {
          const cost = costs?.[v.cause] ?? 0;
          return (
            <div className="split-row" key={v.cause} title={v.description}>
              <span className="split-label">{v.label}</span>
              <span className="split-bar" style={{ width: `${(100 * cost) / max}%` }}>
                {cost > 0 && <span className="split-segment" style={{ flex: 1, background: v.colour }} />}
              </span>
              <span className="split-total">{cost > 0 ? `−${Math.round(cost)}%` : '—'}</span>
            </div>
          );
        })}
      </div>
    </Frame>
  );
};

// The ending: how it went (the case) and the errors in it; its type and moves
// are the section heading's (2026-10-10). The period's record in the same
// type is the second row's (`TypeRecordCard`).
const EndingCard: React.FC<{ loading: true; game?: undefined } | { loading?: false; game: LastGame }> = (props) => {
  const title = 'The ending';
  if (props.loading) {
    return (
      <Frame title={title}>
        <p className="last-game-third-head">
          <TextSkeleton sample="Lost on time" />
        </p>
        <SkeletonRows labels={['Won positions let slip', 'Blunders', 'Misses']} value="0" />
      </Frame>
    );
  }
  const { game } = props;
  const detail = game.ending;
  const errors = game.errors;
  const kase = detail?.endingCase ? endingCaseView(detail.endingCase) : undefined;
  const rows = [
    { key: 'thrown', label: 'Won positions let slip', count: errors?.thrown ?? 0, colour: 'var(--viz-ending-slipped)' },
    { key: 'blunders', label: 'Blunders', count: errors?.blunders ?? 0, colour: 'var(--viz-loss)' },
    { key: 'misses', label: 'Misses', count: errors?.misses ?? 0, colour: 'var(--viz-ending-missed)' },
  ];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Frame title={title}>
      {kase && (
        <p className="last-game-third-head" title={kase.description}>
          <span style={{ color: kase.colour }}>{kase.label}</span>
        </p>
      )}
      <div className="split-rows">
        {rows.map((r) => (
          <div className="split-row" key={r.key}>
            <span className="split-label">{r.label}</span>
            <span className="split-bar" style={{ width: `${(100 * r.count) / max}%` }}>
              {r.count > 0 && <span className="split-segment" style={{ flex: 1, background: r.colour }} />}
            </span>
            <span className="split-total">{r.count}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
};
